import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, getToken, API, formatApiErrorDetail, openDownload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtEuro, fmtDateTime, fmtDate, isInternal, cleanPhone, DOC_TAG_LABELS, ROLE_LABELS, isQuinto } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Pencil, Lock, Globe, Upload, FileText, Download, Trash2, User, Building2, History, Send, Eye, UserCheck, MapPin, CalendarClock, Mail, Contact, MessageSquare, Euro, ArrowDownLeft, ArrowUpRight, Megaphone, Check, X, Plus, Wand2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { WhatsAppLink } from "@/components/WhatsAppLink";
import { CallButton, CallLog } from "@/components/Romulus";
import { CompiledDocsButton } from "@/components/DocTemplates";
import { SignRequestsCard } from "@/components/SignRequests";
import { PracticeEventDialog } from "@/components/PracticeEventDialog";
import { SmsButton } from "@/components/SmsButton";
import { CoobbligatoCard } from "@/components/CoobbligatoCard";
import { toast } from "sonner";
import { useFileNamePrompt, renamedFile } from "@/components/FileNamePrompt";

const CAL_TIPO = { appuntamento: "Appuntamento", richiamo: "Richiamo", manuale: "Manuale", compito: "Compito" };

export default function PracticeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const internal = isInternal(user?.role);
  const [p, setP] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [docTags, setDocTags] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [pubNote, setPubNote] = useState("");
  const [noteReserved, setNoteReserved] = useState(false);
  const [uploadTag, setUploadTag] = useState("ALTRO");
  const [askName, nameDialog] = useFileNamePrompt();
  const renameDoc = async (d) => {
    const name = await askName(d.original_filename, "Rinomina file");
    if (!name || name === d.original_filename) return;
    try { await api.patch(`/practices/${id}/documents/${d.id}`, { name }); toast.success("File rinominato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore"); }
  };
  const [internalUsers, setInternalUsers] = useState([]);
  const [assignee, setAssignee] = useState("none");
  const [sedi, setSedi] = useState([]);
  const [sedeVal, setSedeVal] = useState("none");
  const [scadenzaVal, setScadenzaVal] = useState("");
  const [preview, setPreview] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [calEvents, setCalEvents] = useState([]);
  const [eventOpen, setEventOpen] = useState(false);
  const loadEvents = useCallback(() => api.get("/calendar", { params: { practice_id: id } }).then((r) => setCalEvents(r.data)).catch(() => {}), [id]);
  const [clientOpen, setClientOpen] = useState(false);
  const [cf, setCf] = useState({});
  const [importoOpen, setImportoOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [ff, setFf] = useState({});
  const [importoVal, setImportoVal] = useState("");
  const [products, setProducts] = useState([]);
  const [prodottoOpen, setProdottoOpen] = useState(false);
  const [prodottoVal, setProdottoVal] = useState("");
  const [partners, setPartners] = useState([]);
  const [partyUser, setPartyUser] = useState(null);
  const [numIst, setNumIst] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [istituti, setIstituti] = useState([]);
  const [daLead, setDaLead] = useState(false);
  const [campagnaVal, setCampagnaVal] = useState("none");
  const [provTipo, setProvTipo] = useState("entrata");
  const [provSogg, setProvSogg] = useState("");
  const [provImporto, setProvImporto] = useState("");
  const [provData, setProvData] = useState("");
  const [provNote, setProvNote] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [leadVal, setLeadVal] = useState("none");
  const [logOpen, setLogOpen] = useState(false);
  const [anagResults, setAnagResults] = useState([]);

  const searchAnag = async (term) => {
    try { const { data } = await api.get("/anagrafiche", { params: term ? { q: term } : {} }); setAnagResults(data); }
    catch { setAnagResults([]); }
  };
  const linkAnag = async (aid) => {
    if (!aid || (p.anagrafiche_ids || []).includes(aid)) return;
    try { const { data } = await api.put(`/practices/${id}`, { anagrafiche_ids: [...(p.anagrafiche_ids || []), aid] }); setP(data); toast.success("Anagrafica collegata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const detachAnag = async (aid) => {
    try { const { data } = await api.put(`/practices/${id}`, { anagrafiche_ids: (p.anagrafiche_ids || []).filter((x) => x !== aid) }); setP(data); toast.success("Anagrafica scollegata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const searchLeads = async (term) => {
    try { const { data } = await api.get("/leads", { params: term ? { q: term } : {} }); setLeadResults(data); }
    catch { setLeadResults([]); }
  };

  const saveProvenienza = async () => {
    try { const { data } = await api.put(`/practices/${id}`, { da_lead: daLead, campagna_id: campagnaVal === "none" ? "" : campagnaVal, lead_id: leadVal === "none" ? "" : leadVal }); setP(data); toast.success("Provenienza aggiornata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateProvStato = async (provId, stato) => {
    try { const { data } = await api.put(`/practices/${id}/provvigioni/${provId}`, { stato }); setP(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [provHint, setProvHint] = useState("");
  const pickProvSogg = async (sid) => {
    setProvSogg(sid); setProvHint("");
    if (provTipo !== "uscita") return;
    try {
      const { data } = await api.get(`/practices/${id}/provvigioni/proposta`, { params: { soggetto_id: sid } });
      if (data.importo != null) { setProvImporto(String(data.importo)); setProvHint(`Proposta: ${data.percentuale}% di ${fmtEuro(data.base)} in entrata`); }
    } catch { /* nessuna proposta */ }
  };

  const addProvvigione = async () => {
    if (!provSogg || !provImporto) { toast.error("Seleziona soggetto e importo"); return; }
    try {
      const { data } = await api.post(`/practices/${id}/provvigioni`, { tipo: provTipo, soggetto_id: provSogg, importo: parseFloat(provImporto), data: provData || undefined, note: provNote || undefined });
      setP(data); setProvSogg(""); setProvImporto(""); setProvData(""); setProvNote(""); setProvHint(""); toast.success("Provvigione aggiunta");
    } catch { toast.error("Errore"); }
  };

  const deleteProvvigione = async (provId) => {
    try { const { data } = await api.delete(`/practices/${id}/provvigioni/${provId}`); setP(data); toast.success("Provvigione rimossa"); }
    catch { toast.error("Errore"); }
  };

  const saveNumIst = async () => {
    try { const { data } = await api.put(`/practices/${id}`, { numero_istituto: numIst }); setP(data); toast.success("N. Pratica Istituto salvato"); }
    catch (e) { toast.error("Errore"); }
  };

  const changePartner = async (field, val) => {
    try {
      const { data } = await api.put(`/practices/${id}`, { [field]: val === "none" ? "" : val });
      setP(data); toast.success("Pratica aggiornata");
    } catch (e) { toast.error("Errore"); }
  };

  const saveClient = async () => {
    try {
      const anag = (p.anagrafiche || [])[0];
      if (anag) {
        const tel = anag.cellulare || !anag.telefono ? { cellulare: cf.telefono } : { telefono: cf.telefono };
        await api.put(`/anagrafiche/${anag.id}`, { ...anag, tipo: cf.soggetto_tipo || anag.tipo, nome: cf.cliente_nome, cognome: cf.cliente_cognome, ragione_sociale: cf.ragione_sociale, email: cf.email, ...tel });
      }
      await api.put(`/practices/${id}`, cf);
      setClientOpen(false); load(); toast.success(anag ? "Dati cliente e anagrafica aggiornati" : "Dati cliente aggiornati");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveFinance = async () => {
    try {
      const payload = {
        valore_bene: ff.valore_bene !== "" && ff.valore_bene != null ? parseFloat(ff.valore_bene) : null,
        anticipo: ff.anticipo !== "" && ff.anticipo != null ? parseFloat(ff.anticipo) : null,
        spese_istruttoria: ff.spese_istruttoria !== "" && ff.spese_istruttoria != null ? parseFloat(ff.spese_istruttoria) : null,
        durata: ff.durata !== "" && ff.durata != null ? parseInt(ff.durata, 10) : null,
        montante: ff.montante !== "" && ff.montante != null ? parseFloat(ff.montante) : null,
        tabella_finanziaria: ff.tabella_finanziaria || "",
        assicurazione_cpi: ff.assicurazione_cpi || "",
        assicurazione_cvt: ff.assicurazione_cvt || "",
      };
      const { data } = await api.put(`/practices/${id}`, payload);
      setP(data); setFinanceOpen(false); toast.success("Dettagli finanziamento aggiornati");
    } catch (e) { toast.error("Errore"); }
  };

  const saveProdotto = async () => {
    try {
      const { data } = await api.put(`/practices/${id}`, { tipo_finanziamento: prodottoVal });
      setP(data); setProdottoOpen(false); toast.success("Prodotto aggiornato");
    } catch { toast.error("Errore aggiornamento prodotto"); }
  };

  const saveImporto = async () => {
    const val = parseFloat(String(importoVal).replace(",", "."));
    if (isNaN(val) || val < 0) { toast.error("Importo non valido"); return; }
    try {
      const { data } = await api.put(`/practices/${id}`, { importo: val });
      setP(data); setImportoOpen(false); toast.success("Importo aggiornato");
    } catch (e) { toast.error("Errore"); }
  };

  const load = useCallback(() => {
    api.get(`/practices/${id}`).then((r) => { setP(r.data); setNewStatus(r.data.status); setAssignee(r.data.assigned_to || "none"); setSedeVal(r.data.sede_id || "none"); setScadenzaVal(r.data.data_scadenza || ""); setNumIst(r.data.numero_istituto || ""); setDaLead(!!r.data.da_lead); setCampagnaVal(r.data.campagna_id || "none"); setLeadVal(r.data.lead_id || "none"); if (r.data.lead_id && r.data.lead_nome) setLeadResults([{ id: r.data.lead_id, nome: r.data.lead_nome }]); })
      .catch(() => { toast.error("Pratica non accessibile"); navigate("/pratiche"); });
    api.get(`/practices/${id}/email-log`).then((r) => setEmailLog(r.data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    api.get("/meta").then((r) => { setStatuses(r.data.statuses); setDocTags(r.data.doc_tags); setProducts(r.data.products || []); });
    if (user?.role === "admin") api.get("/users/internal").then((r) => setInternalUsers(r.data));
    if (isInternal(user?.role)) api.get("/sedi").then((r) => setSedi(r.data));
    if (isInternal(user?.role)) api.get("/users/partners").then((r) => setPartners(r.data)).catch(() => {});
    if (isInternal(user?.role)) api.get("/users/istituti").then((r) => setIstituti(r.data)).catch(() => {});
    if (isInternal(user?.role)) api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    if (isInternal(user?.role)) loadEvents();
    load();
  }, [load, loadEvents, user]);

  const changeSede = async (val) => {
    setSedeVal(val);
    try {
      const { data } = await api.put(`/practices/${id}`, { sede_id: val === "none" ? "" : val });
      setP(data);
      toast.success("Sede aggiornata");
    } catch (e) { toast.error("Errore"); }
  };

  const changeScadenza = async (val) => {
    setScadenzaVal(val);
    try {
      const { data } = await api.put(`/practices/${id}`, { data_scadenza: val || "" });
      setP(data);
      toast.success("Scadenza aggiornata");
    } catch (e) { toast.error("Errore"); }
  };

  const assign = async (val) => {
    setAssignee(val);
    try {
      const { data } = await api.post(`/practices/${id}/assign`, { user_id: val === "none" ? null : val });
      setP(data);
      toast.success(val === "none" ? "Assegnazione rimossa" : "Pratica assegnata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const openPreview = async (d) => {
    try {
      const resp = await api.get(`/practices/${id}/documents/${d.id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(resp.data), type: d.content_type || "", name: d.original_filename });
    } catch (e) { toast.error("Impossibile aprire l'anteprima"); }
  };

  const changeStatus = async () => {
    try {
      const { data } = await api.post(`/practices/${id}/status`, { status: newStatus, nota: statusNote });
      setP(data); setStatusNote("");
      toast.success(`Stato aggiornato: ${newStatus}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const addNote = async (text, visibility, reset) => {
    if (!text.trim()) return;
    try {
      await api.post(`/practices/${id}/notes`, { text, visibility });
      reset(""); load();
      toast.success("Nota aggiunta");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const upload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Il file supera la dimensione massima di 20MB");
      e.target.value = "";
      return;
    }
    e.target.value = "";
    const name = await askName(file.name);
    if (!name) return;
    file = renamedFile(file, name);
    const fd = new FormData();
    fd.append("file", file); fd.append("tag", uploadTag);
    try {
      await api.post(`/practices/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load(); toast.success("Documento caricato");
    } catch (err) { toast.error(err.response?.status === 413 ? "Il file supera la dimensione massima di 20MB" : "Errore upload"); }
  };

  const autofillDoc = async (d) => {
    try {
      const { data } = await api.post(`/practices/${id}/documents/${d.id}/autofill`);
      if (!data.template) toast.info("Il file non corrisponde a nessun modello documento");
      else if (!data.fields) toast.info(`${data.template}: nessun campo vuoto da completare`);
      else { toast.success(`${data.template}: ${data.fields === 1 ? "completato 1 campo" : `completati ${data.fields} campi`}`); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Completamento non riuscito"); }
  };
  const viewDoc = (docId) => openDownload(`/practices/${id}/documents/${docId}/download`);
  const deleteDoc = async (docId) => { await api.delete(`/practices/${id}/documents/${docId}`); load(); toast.success("Documento eliminato"); };
  const deleteNote = async (noteId) => {
    try { const { data } = await api.delete(`/practices/${id}/notes/${noteId}`); setP(data); toast.success("Nota eliminata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const editNote = async (noteId, text) => {
    try { const { data } = await api.put(`/practices/${id}/notes/${noteId}`, { text }); setP(data); toast.success("Nota aggiornata"); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); return false; }
  };

  if (!p) return <div className="text-muted-foreground">Caricamento…</div>;

  const publicNotes = p.notes.filter((n) => n.visibility === "public");
  const allNotes = (internal ? p.notes : publicNotes)
    .slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const anagName = (a) => a.tipo === "giuridica" ? a.ragione_sociale : `${a.nome || ""} ${a.cognome || ""}`.trim();
  const convenzionati = partners.filter((u) => u.role === "convenzionato");
  const collaboratori = partners.filter((u) => u.role === "collaboratore");
  const segnalatori = partners.filter((u) => u.role === "segnalatore");
  const partyConv = partners.find((u) => u.id === p.convenzionato_id);
  const partyCollab = partners.find((u) => u.id === p.collaboratore_id);
  const partySegn = partners.find((u) => u.id === (p.segnalatore_id || p.owner_id)) || null;
  const prov = p.provvigioni || [];
  const myProv = internal ? prov : prov.filter((x) => x.soggetto_id === user?.id);
  const totIn = prov.filter((x) => x.tipo === "entrata").reduce((s, x) => s + (x.importo || 0), 0);
  const totOut = prov.filter((x) => x.tipo === "uscita").reduce((s, x) => s + (x.importo || 0), 0);
  const usciteOptions = [partyConv, partyCollab, partySegn].filter(Boolean);
  const firstAnag = (p.anagrafiche || [])[0] || null;
  const cd = firstAnag ? {
    nome: firstAnag.nome, cognome: firstAnag.cognome, ragione_sociale: firstAnag.ragione_sociale,
    soggetto_tipo: firstAnag.tipo, email: firstAnag.email,
    telefono: firstAnag.cellulare || firstAnag.telefono || firstAnag.telefono_fisso, canale: p.canale,
  } : {
    nome: p.cliente_nome, cognome: p.cliente_cognome, ragione_sociale: p.ragione_sociale,
    soggetto_tipo: p.soggetto_tipo, email: p.email, telefono: p.telefono, canale: p.canale,
  };

  return (
    <div className="space-y-6">
      {nameDialog}
      <button onClick={() => navigate("/pratiche")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-practices">
        <ArrowLeft className="h-4 w-4" /> Torna alle pratiche
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            {p.numero_istituto ? (
              <>
                <span className="font-mono text-base text-primary font-semibold" data-testid="header-numero-istituto">{p.numero_istituto}</span>
                <span className="font-mono text-xs text-muted-foreground" data-testid="header-numero">ID: {p.numero}</span>
              </>
            ) : (
              <span className="font-mono text-sm text-primary font-medium" data-testid="header-numero">{p.numero}</span>
            )}
            {p.istituto && <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium" data-testid="header-istituto">{p.istituto}</span>}
            <StatusBadge status={p.status} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{(() => {
            const a = (p.anagrafiche || [])[0];
            if (a) {
              const nm = a.tipo === "giuridica" ? (a.ragione_sociale || "") : `${a.nome || ""} ${a.cognome || ""}`.trim();
              if (nm) return nm;
            }
            return p.titolo;
          })()}</h1>
          <p className="text-sm text-muted-foreground mt-1">{p.tipo_finanziamento}
            {internal && (
              <button type="button" data-testid="edit-prodotto-btn" onClick={() => { setProdottoVal(p.tipo_finanziamento || ""); setProdottoOpen(true); }} className="ml-1.5 inline-flex items-center text-primary hover:text-primary/80 align-middle" title="Cambia prodotto">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {p.istituto ? ` · ${p.istituto}` : ""} · <span className="font-semibold text-foreground" data-testid="practice-importo">{fmtEuro(p.importo)}</span>
            {p.valore_bene != null && p.valore_bene !== "" && <span className="ml-1 text-xs text-muted-foreground" data-testid="practice-importo-hint">(valore bene − anticipo)</span>}
            {internal && (
              <button type="button" data-testid="edit-importo-btn" onClick={() => { setImportoVal(p.importo != null ? String(p.importo) : ""); setImportoOpen(true); }} className="ml-2 inline-flex items-center text-primary hover:text-primary/80 align-middle" title="Modifica importo">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </p>
          {(p.convenzionato_name || p.collaboratore_name || p.segnalatore_name) && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="involved-parties">
              {p.convenzionato_name && (
                <button type="button" onClick={() => partyConv && setPartyUser(partyConv)} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs transition-colors hover:bg-primary/15 disabled:cursor-default disabled:hover:bg-secondary" disabled={!partyConv} data-testid="party-convenzionato">
                  <Contact className="h-3.5 w-3.5 text-primary" /><span className="text-muted-foreground">Convenzionato:</span> <span className="font-medium">{p.convenzionato_name}</span>
                </button>
              )}
              {p.collaboratore_name && (
                <button type="button" onClick={() => partyCollab && setPartyUser(partyCollab)} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs transition-colors hover:bg-primary/15 disabled:cursor-default disabled:hover:bg-secondary" disabled={!partyCollab} data-testid="party-collaboratore">
                  <UserCheck className="h-3.5 w-3.5 text-primary" /><span className="text-muted-foreground">Collaboratore:</span> <span className="font-medium">{p.collaboratore_name}</span>
                </button>
              )}
              {p.segnalatore_name && (
                <button type="button" onClick={() => partySegn && setPartyUser(partySegn)} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs transition-colors hover:bg-primary/15 disabled:cursor-default disabled:hover:bg-secondary" disabled={!partySegn} data-testid="party-segnalatore">
                  <User className="h-3.5 w-3.5 text-primary" /><span className="text-muted-foreground">Segnalatore:</span> <span className="font-medium">{p.segnalatore_name}</span>
                </button>
              )}
            </div>
          )}
        </div>
        {internal && <Button variant="outline" size="sm" data-testid="practice-log-btn" onClick={() => setLogOpen(true)}><History className="h-4 w-4 mr-1.5" />Log attività</Button>}
      </div>

      {(internal || p.cliente_nome || p.ragione_sociale || p.email || p.telefono || p.canale || p.istituto) && (
        <Card className="p-5" data-testid="practice-client-card">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h2 className="font-heading font-semibold flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Dati Cliente{firstAnag && <span className="text-xs font-normal text-muted-foreground">· da anagrafica collegata</span>}</h2>
            <div className="flex items-center gap-2">
              {(firstAnag?.cellulare || cd.telefono) && <SmsButton size="sm" recipients={[{ name: `${cd.nome || ""} ${cd.cognome || ""}`.trim() || cd.ragione_sociale || p.titolo, number: firstAnag?.cellulare || cd.telefono }]} defaultMessage={`Buongiorno ${cd.nome || ""},`.trim()} context={{ type: "practice", id: p.id }} />}
              {internal && (
                <Button variant="outline" size="sm" data-testid="edit-client-btn" onClick={() => { setCf({ cliente_nome: cd.nome || "", cliente_cognome: cd.cognome || "", ragione_sociale: cd.ragione_sociale || "", email: cd.email || "", telefono: cd.telefono || "", canale: cd.canale || "", soggetto_tipo: cd.soggetto_tipo || "fisica" }); setClientOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {(cd.nome || cd.cognome) && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Nome</span><span className="font-medium text-right">{`${cd.nome || ""} ${cd.cognome || ""}`.trim()}</span></div>}
            {cd.ragione_sociale && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Ragione Sociale</span><span className="font-medium text-right">{cd.ragione_sociale}</span></div>}
            {cd.soggetto_tipo && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Soggetto</span><span className="font-medium">{cd.soggetto_tipo === "giuridica" ? "Partita IVA" : "Persona Fisica"}</span></div>}
            {cd.email && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Email</span><a href={`mailto:${cd.email}`} className="font-medium text-primary truncate">{cd.email}</a></div>}
            {cd.telefono && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Telefono</span><span className="inline-flex items-center gap-1.5"><a href={`tel:${cleanPhone(cd.telefono)}`} className="font-medium text-primary">{cleanPhone(cd.telefono)}</a>{(firstAnag?.cellulare || cd.telefono) && <WhatsAppLink number={firstAnag?.cellulare || cd.telefono} />}{internal && <CallButton phone={cleanPhone(cd.telefono)} practiceId={p.id} contactName={p.titolo} size="sm" className="h-7 px-2 text-xs" testid="practice-call-btn" />}</span></div>}
            {cd.canale && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Canale</span><span className="font-medium">{cd.canale}</span></div>}
            {p.istituto && <div className="flex justify-between gap-2" data-testid="client-istituto"><span className="text-muted-foreground">Istituto finanziario</span><span className="font-medium text-right">{p.istituto}</span></div>}
            {p.provincia && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Provincia</span><span className="font-medium">{p.provincia}</span></div>}
            {internal && p.agente && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Agente attuale</span><span className="font-medium text-right">{p.agente}</span></div>}
            {internal && p.in_carico_a && <div className="flex justify-between gap-2"><span className="text-muted-foreground">In carico a</span><span className="font-medium text-right">{p.in_carico_a}</span></div>}
            {!(cd.nome || cd.cognome || cd.ragione_sociale || cd.email || cd.telefono || cd.canale) && <p className="text-muted-foreground py-2 sm:col-span-2">{firstAnag ? "L'anagrafica collegata non ha dati di contatto." : "Nessun dato cliente. Clicca \"Modifica\" per aggiungerli."}</p>}
          </div>
        </Card>
      )}

      <CoobbligatoCard practice={p} onSaved={setP} />

      {(isQuinto(p.tipo_finanziamento) || ["Prestito Auto", "Altro Finalizzato", "Fotovoltaico"].includes(p.tipo_finanziamento) || (p.montante != null && p.montante !== "") || (p.valore_bene != null && p.valore_bene !== "") || p.anticipo || p.spese_istruttoria || p.durata || p.tabella_finanziaria || p.assicurazione_cpi || p.assicurazione_cvt) ? (
        <Card className="p-5" data-testid="practice-financing-card">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h2 className="font-heading font-semibold flex items-center gap-2"><Euro className="h-4 w-4 text-primary" /> Dettagli Finanziamento</h2>
            {internal && (
              <Button variant="outline" size="sm" data-testid="edit-finance-btn" onClick={() => { setFf({ valore_bene: p.valore_bene != null ? String(p.valore_bene) : "", anticipo: p.anticipo != null ? String(p.anticipo) : "", spese_istruttoria: p.spese_istruttoria != null ? String(p.spese_istruttoria) : "", durata: p.durata != null ? String(p.durata) : "", montante: p.montante != null ? String(p.montante) : "", tabella_finanziaria: p.tabella_finanziaria || "", assicurazione_cpi: p.assicurazione_cpi || "", assicurazione_cvt: p.assicurazione_cvt || "" }); setFinanceOpen(true); }}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {p.montante != null && p.montante !== "" && <div className="flex justify-between gap-2" data-testid="practice-montante"><span className="text-muted-foreground">Montante</span><span className="font-medium">{fmtEuro(p.montante)}</span></div>}
            {p.valore_bene != null && p.valore_bene !== "" && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Valore bene</span><span className="font-medium">{fmtEuro(p.valore_bene)}</span></div>}
            {p.anticipo != null && p.anticipo !== "" && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Anticipo</span><span className="font-medium">{fmtEuro(p.anticipo)}</span></div>}
            {p.durata != null && p.durata !== "" && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Durata</span><span className="font-medium">{p.durata} mesi</span></div>}
            {p.spese_istruttoria != null && p.spese_istruttoria !== "" && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Spese Istruttoria</span><span className="font-medium">{fmtEuro(p.spese_istruttoria)}</span></div>}
            {p.tabella_finanziaria && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Tabella finanziaria</span><span className="font-medium text-right">{p.tabella_finanziaria}</span></div>}
            {p.assicurazione_cpi && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Assicurazione CPI</span><span className="font-medium text-right">{p.assicurazione_cpi}</span></div>}
            {p.assicurazione_cvt && <div className="flex justify-between gap-2"><span className="text-muted-foreground">Assicurazione CVT</span><span className="font-medium text-right">{p.assicurazione_cvt}</span></div>}
            {!((p.montante != null && p.montante !== "") || (p.valore_bene != null && p.valore_bene !== "") || p.anticipo || p.spese_istruttoria || p.durata || p.tabella_finanziaria || p.assicurazione_cpi || p.assicurazione_cvt) && <p className="text-muted-foreground py-2 sm:col-span-2">Nessun dettaglio. Clicca "Modifica" per aggiungerli.</p>}
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left */}
        <div className="lg:col-span-8 space-y-6">
          {/* Notes — unico gruppo cronologico */}
          <Card className="p-5">
            <div className="space-y-4">
              <h3 className="font-heading font-semibold flex items-center gap-2 text-sm" data-testid="notes-heading"><MessageSquare className="h-4 w-4 text-primary" /> Note ({allNotes.length})</h3>
              <div className="space-y-2">
                <Textarea data-testid="note-input" value={pubNote} onChange={(e) => setPubNote(e.target.value)} placeholder={noteReserved ? "Nota riservata interna…" : "Scrivi una nota visibile ai soggetti coinvolti…"} className="min-h-[60px]" />
                <div className="flex items-center justify-between gap-3">
                  {internal ? (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="note-reserved-toggle">
                      <Checkbox checked={noteReserved} onCheckedChange={(v) => setNoteReserved(!!v)} data-testid="note-reserved-checkbox" />
                      <Lock className="h-3.5 w-3.5 text-amber-500" /> Nota riservata (solo interni)
                    </label>
                  ) : <span />}
                  <Button data-testid="add-note-btn" onClick={() => addNote(pubNote, noteReserved ? "internal" : "public", (v) => { setPubNote(v); })} className="self-end"><Send className="h-4 w-4 mr-2" />Aggiungi</Button>
                </div>
              </div>
              <NoteList notes={allNotes} canManage={internal} onDelete={deleteNote} onEdit={editNote} />
            </div>
          </Card>

          {/* Cronologia Stati (sotto le note) */}
          <Card className="p-5">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Cronologia Stati</h2>
            <div className="space-y-3">
              {[...(p.status_history || [])].reverse().map((h, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />
                    {i < p.status_history.length - 1 && <span className="w-px flex-1 bg-border" />}
                  </div>
                  <div className="pb-2">
                    <p className="text-sm font-medium">{h.status}</p>
                    <p className="text-xs text-muted-foreground">{h.changed_by} · {fmtDateTime(h.changed_at)}</p>
                    {h.nota && <p className="text-xs text-muted-foreground mt-0.5 italic">"{h.nota}"</p>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {internal && (
            <Card className="p-5" data-testid="practice-events-card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Appuntamenti collegati ({calEvents.length})</h2>
                <Button size="sm" variant="outline" onClick={() => setEventOpen(true)} data-testid="practice-new-event-btn"><Plus className="h-4 w-4 mr-1.5" />Nuovo evento</Button>
              </div>
              {eventOpen && <PracticeEventDialog practice={p} client={cd} userId={user?.id} sedi={sedi} onClose={() => setEventOpen(false)} onCreated={loadEvents} />}
              {calEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun appuntamento o compito collegato a questa pratica.</p>
              ) : (
                <div className="divide-y divide-border">
                  {calEvents.slice().sort((a, b) => (a.start || "").localeCompare(b.start || "")).map((e) => (
                    <button key={e.id} data-testid={`practice-event-${e.id}`} onClick={() => navigate("/calendario")} className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-accent/40 rounded-md px-1 -mx-1">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${e.done ? "bg-muted-foreground" : "bg-primary"}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${e.done ? "line-through text-muted-foreground" : ""}`}>{e.titolo}</p>
                        <p className="text-xs text-muted-foreground truncate">{CAL_TIPO[e.tipo] || e.tipo}{e.assigned_name ? ` · ${e.assigned_name}` : ""}{e.done ? " · completato" : ""}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{(e.start || "").slice(11, 16) === "00:00" ? fmtDate(e.start) : fmtDateTime(e.start)}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          )}

          {(internal || myProv.length > 0) && (
            <Card className="p-5" data-testid="provvigioni-card">
              <h2 className="font-heading font-semibold mb-4 flex items-center gap-2"><Euro className="h-4 w-4 text-primary" /> Provvigioni</h2>
              {internal && (<>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                  <p className="text-xs text-muted-foreground">Entrate</p>
                  <p className="text-base font-bold text-emerald-600 dark:text-emerald-400" data-testid="prov-tot-in">{fmtEuro(totIn)}</p>
                </div>
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
                  <p className="text-xs text-muted-foreground">Uscite</p>
                  <p className="text-base font-bold text-rose-600 dark:text-rose-400" data-testid="prov-tot-out">{fmtEuro(totOut)}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-xs text-muted-foreground">Netto</p>
                  <p className="text-base font-bold" data-testid="prov-tot-net">{fmtEuro(totIn - totOut)}</p>
                </div>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-3 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={provTipo} onValueChange={(v) => { setProvTipo(v); setProvSogg(""); setProvHint(""); }}>
                    <SelectTrigger data-testid="prov-tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrata">Entrata (da Istituto)</SelectItem>
                      <SelectItem value="uscita">Uscita (a soggetto)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={provSogg} onValueChange={pickProvSogg}>
                    <SelectTrigger data-testid="prov-soggetto-select"><SelectValue placeholder="Soggetto" /></SelectTrigger>
                    <SelectContent>
                      {provTipo === "entrata"
                        ? (istituti.length ? istituti.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>) : <div className="px-2 py-1.5 text-xs text-muted-foreground">Nessun istituto configurato</div>)
                        : (usciteOptions.length ? usciteOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</SelectItem>) : <div className="px-2 py-1.5 text-xs text-muted-foreground">Nessun soggetto coinvolto</div>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Importo €" data-testid="prov-importo-input" value={provImporto} onChange={(e) => setProvImporto(e.target.value)} />
                  <Input type="date" data-testid="prov-data-input" value={provData} onChange={(e) => setProvData(e.target.value)} />
                </div>
                {provHint && <p className="text-xs text-primary" data-testid="prov-proposta-hint">{provHint}</p>}
                <Input placeholder="Note (opzionale)" data-testid="prov-note-input" value={provNote} onChange={(e) => setProvNote(e.target.value)} />
                <Button className="w-full" data-testid="prov-add-btn" onClick={addProvvigione}><Send className="h-4 w-4 mr-2" />Aggiungi provvigione</Button>
              </div>
              </>)}
              <div className="space-y-2" data-testid="prov-list">
                {myProv.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessuna provvigione registrata</p>}
                {myProv.map((x) => (
                  <div key={x.id} data-testid={`prov-item-${x.id}`} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${x.tipo === "entrata" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/15 text-rose-600 dark:text-rose-400"}`}>
                      {x.tipo === "entrata" ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}{x.tipo === "entrata" ? "Entrata" : "Uscita"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{x.soggetto_nome || "—"}</p>
                      <p className="text-xs text-muted-foreground">{x.data}{x.note && ` · ${x.note}`}</p>
                      {internal ? (
                      <Select value={x.stato || "in_lavorazione"} onValueChange={(v) => updateProvStato(x.id, v)}>
                        <SelectTrigger className="h-7 mt-1 w-40 text-xs" data-testid={`prov-stato-select-${x.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                          <SelectItem value="pagato">Pagato</SelectItem>
                          <SelectItem value="annullato">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      ) : (
                      <span className="text-xs mt-1 inline-block px-2 py-0.5 rounded bg-secondary" data-testid={`prov-stato-${x.id}`}>{({ in_lavorazione: "In lavorazione", pagato: "Pagato", annullato: "Annullato" })[x.stato || "in_lavorazione"]}</span>
                      )}
                    </div>
                    <span className={`font-semibold text-sm whitespace-nowrap ${x.stato === "annullato" ? "line-through text-muted-foreground" : ""}`}>{fmtEuro(x.importo)}</span>
                    {internal && <Button variant="ghost" size="icon" data-testid={`prov-delete-${x.id}`} onClick={() => deleteProvvigione(x.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {internal && <CallLog practiceId={p.id} />}
          {internal && <CompiledDocsButton practiceId={p.id} />}
          {internal && <SignRequestsCard practice={p} onSigned={load} />}

          {/* Documents */}
          <Card className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-heading font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Documenti Allegati ({p.documents.length})</h2>
              <div className="flex gap-2">
                <Select value={uploadTag} onValueChange={setUploadTag}>
                  <SelectTrigger className="w-44" data-testid="document-tag-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{docTags.map((t) => <SelectItem key={t} value={t}>{DOC_TAG_LABELS[t]}</SelectItem>)}</SelectContent>
                </Select>
                <Button asChild variant="outline">
                  <label className="cursor-pointer" data-testid="document-upload-label">
                    <Upload className="h-4 w-4 mr-2" /> Carica
                    <input type="file" className="hidden" data-testid="document-upload-input" onChange={upload} />
                  </label>
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {p.documents.map((d) => (
                <div key={d.id} data-testid={`document-item-${d.id}`} className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-accent/40">
                  <div className="flex h-9 w-9 items-center justify-center rounded bg-primary/10 text-primary shrink-0"><FileText className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{d.original_filename}</p>
                    <p className="text-xs text-muted-foreground">{DOC_TAG_LABELS[d.tag] || d.tag} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by}{d.autofilled_template && <span className="text-primary" data-testid={`document-autofilled-${d.id}`}> · completato dalla pratica ({d.autofilled_template})</span>}</p>
                  </div>
                  <Button variant="ghost" size="icon" data-testid={`document-preview-${d.id}`} onClick={() => openPreview(d)}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`document-view-${d.id}`} onClick={() => viewDoc(d.id)}><Download className="h-4 w-4" /></Button>
                  {internal && d.content_type === "application/pdf" && <Button variant="ghost" size="icon" data-testid={`document-autofill-${d.id}`} title="Completa con i dati della pratica" onClick={() => autofillDoc(d)}><Wand2 className="h-4 w-4" /></Button>}
                  {(internal || d.uploaded_by === user?.name) && <Button variant="ghost" size="icon" data-testid={`document-rename-${d.id}`} title="Rinomina" onClick={() => renameDoc(d)}><Pencil className="h-4 w-4" /></Button>}
                  {internal && <Button variant="ghost" size="icon" data-testid={`document-delete-${d.id}`} onClick={() => deleteDoc(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              ))}
              {p.documents.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun documento allegato</p>}
            </div>
          </Card>
        </div>

        {/* Right */}
        <div className="lg:col-span-4 space-y-6">
          {/* Status workflow */}
          {internal ? (
            <Card className="p-5 space-y-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Cambia Stato</h2>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="practice-detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea data-testid="status-change-note" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Nota sul cambio di stato (opzionale)" className="min-h-[50px]" />
              <Button className="w-full" data-testid="practice-detail-change-status-btn" onClick={changeStatus} disabled={newStatus === p.status && !statusNote}>Aggiorna Stato</Button>
            </Card>
          ) : (
            <Card className="p-5">
              <h2 className="font-heading font-semibold mb-2">Stato attuale</h2>
              <StatusBadge status={p.status} />
            </Card>
          )}

          {internal ? (
            <Card className="p-5 space-y-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Sede di Provenienza</h2>
              <Select value={sedeVal} onValueChange={changeSede}>
                <SelectTrigger data-testid="practice-sede-select"><SelectValue placeholder="Nessuna sede" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna sede</SelectItem>
                  {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </Card>
          ) : p.sede_name ? (
            <Card className="p-5">
              <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Sede di Provenienza</h2>
              <p className="text-sm">{p.sede_name}</p>
            </Card>
          ) : null}

          {internal && (
            <Card className="p-5 space-y-3" data-testid="numero-istituto-card">
              <h2 className="font-heading font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> N. Pratica Istituto</h2>
              <div className="flex gap-2">
                <Input data-testid="numero-istituto-input" value={numIst} onChange={(e) => setNumIst(e.target.value)} placeholder="Numero assegnato dall'istituto" onKeyDown={(e) => e.key === "Enter" && saveNumIst()} />
                <Button data-testid="numero-istituto-save" onClick={saveNumIst} disabled={numIst === (p.numero_istituto || "")}>Salva</Button>
              </div>
            </Card>
          )}

          {internal && (
            <Card className="p-5 space-y-3" data-testid="provenienza-card">
              <h2 className="font-heading font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" /> Provenienza</h2>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="da-lead-toggle">
                <Checkbox checked={daLead} onCheckedChange={(v) => { setDaLead(!!v); if (v && leadResults.length === 0) searchLeads(""); }} data-testid="da-lead-checkbox" /> Proveniente da Lead
              </label>
              {daLead && (
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Lead collegato (rubrica marketing)</label>
                  <Input placeholder="Cerca lead per nome, CF, telefono…" data-testid="lead-search-input" onChange={(e) => searchLeads(e.target.value)} className="mb-1" />
                  <Select value={leadVal} onValueChange={setLeadVal}>
                    <SelectTrigger data-testid="lead-select"><SelectValue placeholder="Seleziona lead" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun lead</SelectItem>
                      {leadResults.map((l) => <SelectItem key={l.id} value={l.id}>{`${l.nome || ""} ${l.cognome || ""}`.trim() || l.codice_fiscale || l.id}{l.codice_fiscale ? ` · ${l.codice_fiscale}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Campagna Marketing</label>
                <Select value={campagnaVal} onValueChange={setCampagnaVal}>
                  <SelectTrigger data-testid="campagna-select"><SelectValue placeholder="Nessuna campagna" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna campagna</SelectItem>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" variant="outline" data-testid="provenienza-save" onClick={saveProvenienza}>Salva provenienza</Button>
            </Card>
          )}

          {/* Scadenza */}
          {internal ? (
            <Card className="p-5 space-y-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Data di Scadenza</h2>
              <Input type="date" data-testid="practice-scadenza-input" value={scadenzaVal} onChange={(e) => changeScadenza(e.target.value)} />
              <p className="text-xs text-muted-foreground">Se la pratica resta Sospesa viene inviato un promemoria ogni 24h; se Approvata, un promemoria ogni 3 mesi.</p>
            </Card>
          ) : p.data_scadenza ? (
            <Card className="p-5">
              <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Scadenza</h2>
              <p className="text-sm">{fmtDate(p.data_scadenza)}</p>
            </Card>
          ) : null}

          {user?.role === "admin" ? (
            <Card className="p-5 space-y-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><UserCheck className="h-4 w-4 text-primary" /> Responsabile Pratica</h2>
              <SearchableSelect value={assignee} onValueChange={assign} testid="practice-assign-select"
                placeholder="Non assegnata" searchPlaceholder="Cerca utente…"
                options={[{ value: "none", label: "Non assegnata" }, ...internalUsers.map((u) => ({ value: u.id, label: u.name }))]} />
            </Card>
          ) : p.assigned_name ? (
            <Card className="p-5">
              <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><UserCheck className="h-4 w-4 text-primary" /> Responsabile</h2>
              <p className="text-sm">{p.assigned_name}</p>
            </Card>
          ) : null}

          {internal && (
            <Card className="p-5 space-y-3" data-testid="reassign-partner-card">
              <h2 className="font-heading font-semibold flex items-center gap-2"><Contact className="h-4 w-4 text-primary" /> Assegnazione (Collaboratore / Convenzionato / Segnalatore)</h2>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Convenzionato</label>
                <SearchableSelect value={p.convenzionato_id || "none"} onValueChange={(v) => changePartner("convenzionato_id", v)}
                  testid="practice-convenzionato-select" placeholder="Nessuno" searchPlaceholder="Cerca convenzionato…"
                  options={[{ value: "none", label: "Nessuno" }, ...convenzionati.map((u) => ({ value: u.id, label: u.name }))]} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Collaboratore</label>
                <SearchableSelect value={p.collaboratore_id || "none"} onValueChange={(v) => changePartner("collaboratore_id", v)}
                  testid="practice-collaboratore-select" placeholder="Nessuno" searchPlaceholder="Cerca collaboratore…"
                  options={[{ value: "none", label: "Nessuno" }, ...collaboratori.map((u) => ({ value: u.id, label: u.name }))]} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Segnalatore</label>
                <SearchableSelect value={p.segnalatore_id || "none"} onValueChange={(v) => changePartner("segnalatore_id", v)}
                  testid="practice-segnalatore-select" placeholder="Nessuno" searchPlaceholder="Cerca segnalatore…"
                  options={[{ value: "none", label: "Nessuno" }, ...segnalatori.map((u) => ({ value: u.id, label: u.name }))]} />
              </div>
            </Card>
          )}

          {/* Anagrafiche */}
          <Card className="p-5">
            <h2 className="font-heading font-semibold mb-3">Anagrafiche Collegate ({p.anagrafiche.length})</h2>
            {internal && (
              <div className="mb-3 space-y-1.5">
                <Input placeholder="Cerca in archivio: nome, cognome, CF…" data-testid="anag-search-input" onChange={(e) => searchAnag(e.target.value)} onFocus={() => anagResults.length === 0 && searchAnag("")} />
                <Select value="none" onValueChange={(v) => v !== "none" && linkAnag(v)}>
                  <SelectTrigger data-testid="anag-link-select"><SelectValue placeholder="Collega anagrafica dall'archivio…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleziona…</SelectItem>
                    {anagResults.filter((a) => !(p.anagrafiche_ids || []).includes(a.id)).map((a) => (
                      <SelectItem key={a.id} value={a.id}>{anagName(a)} · {a.tipo === "giuridica" ? a.partita_iva : a.codice_fiscale || "—"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              {p.anagrafiche.map((a, ai) => (
                <div key={a.id} data-testid={`linked-anagrafica-card-${a.id}`} className="flex items-center gap-3 rounded-lg border border-border p-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded shrink-0 ${a.tipo === "giuridica" ? "bg-indigo-500/10 text-indigo-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                    {a.tipo === "giuridica" ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{anagName(a)}{p.anagrafiche.length > 1 && ai < 2 && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`anag-role-${a.id}`}>{ai === 0 ? "Richiedente" : "Coobbligato"}</span>}</p>
                    <p className="text-xs text-muted-foreground font-mono">{a.tipo === "giuridica" ? `P.IVA ${a.partita_iva || "—"}` : `CF ${a.codice_fiscale || "—"}`}</p>
                  </div>
                  {internal && <Button variant="ghost" size="icon" data-testid={`detach-anagrafica-${a.id}`} onClick={() => detachAnag(a.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              ))}
              {p.anagrafiche.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna anagrafica collegata</p>}
            </div>
          </Card>

          {/* Email log */}
          <Card className="p-5" data-testid="email-log-card">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Storico Invii Email ({emailLog.length})</h2>
            <div className="space-y-2">
              {emailLog.map((e, i) => (
                <div key={i} data-testid={`email-log-item-${i}`} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{e.recipient}</span>
                    {e.failed && <span className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 shrink-0">non recapitata</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{e.heading} · {fmtDateTime(e.sent_at)}</p>
                </div>
              ))}
              {emailLog.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna email inviata (le notifiche vengono raggruppate e inviate ogni 60 minuti)</p>}
            </div>
          </Card>

          {internal && p.client_history?.length > 0 && (
            <Card className="p-5" data-testid="change-history">
              <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Storico Modifiche ({p.client_history.length})</h2>
              <div className="space-y-3">
                {[...p.client_history].reverse().map((h) => (
                  <div key={h.id} className="rounded-lg border border-border p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">{h.changed_by} · {fmtDateTime(h.changed_at)}</p>
                    {h.changes.map((c, i) => (
                      <p key={i} className="text-xs"><span className="font-medium">{c.field}:</span> <span className="line-through text-muted-foreground">{c.old || "—"}</span> → <span className="text-foreground font-medium">{c.new || "—"}</span></p>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="truncate pr-8">{preview?.name}</DialogTitle></DialogHeader>
          {preview && (preview.type.includes("pdf") ? (
            <iframe title="preview" src={preview.url} className="w-full h-[70vh] rounded-md border border-border" data-testid="document-preview-iframe" />
          ) : preview.type.startsWith("image/") ? (
            <img src={preview.url} alt={preview.name} className="max-h-[70vh] mx-auto rounded-md" data-testid="document-preview-image" />
          ) : (
            <div className="py-10 text-center text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Anteprima non disponibile per questo formato.</p>
              <Button className="mt-4" onClick={() => window.open(preview.url, "_blank")}><Download className="h-4 w-4 mr-2" />Scarica file</Button>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={prodottoOpen} onOpenChange={setProdottoOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="prodotto-dialog">
          <DialogHeader><DialogTitle>Cambia Prodotto</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Prodotto</Label>
            <Select value={prodottoVal} onValueChange={setProdottoVal}>
              <SelectTrigger data-testid="prodotto-select"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
              <SelectContent>{products.map((pr) => <SelectItem key={pr} value={pr}>{pr}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdottoOpen(false)}>Annulla</Button>
            <Button data-testid="save-prodotto-btn" onClick={saveProdotto} disabled={!prodottoVal}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importoOpen} onOpenChange={setImportoOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="importo-dialog">
          <DialogHeader><DialogTitle>Modifica Importo</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Importo €</Label>
            <Input type="number" step="0.01" min="0" autoFocus data-testid="importo-input" value={importoVal} onChange={(e) => setImportoVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveImporto()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportoOpen(false)}>Annulla</Button>
            <Button data-testid="save-importo-btn" onClick={saveImporto}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={financeOpen} onOpenChange={setFinanceOpen}>
        <DialogContent data-testid="edit-finance-dialog">
          <DialogHeader><DialogTitle>Modifica Dettagli Finanziamento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {isQuinto(p?.tipo_finanziamento) && <div className="space-y-1.5"><Label>Montante (€)</Label><Input data-testid="finance-montante" type="number" step="0.01" value={ff.montante ?? ""} onChange={(e) => setFf({ ...ff, montante: e.target.value })} /></div>}
            <div className="space-y-1.5"><Label>Durata (mesi)</Label><Input data-testid="finance-durata" type="number" value={ff.durata ?? ""} onChange={(e) => setFf({ ...ff, durata: e.target.value })} placeholder="es. 36" /></div>
            {!isQuinto(p?.tipo_finanziamento) && <>
            <div className="space-y-1.5"><Label>Valore bene (€)</Label><Input data-testid="finance-valore-bene" type="number" step="0.01" value={ff.valore_bene ?? ""} onChange={(e) => setFf({ ...ff, valore_bene: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Anticipo (€)</Label><Input data-testid="finance-anticipo" type="number" step="0.01" value={ff.anticipo ?? ""} onChange={(e) => setFf({ ...ff, anticipo: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Spese Istruttoria (€)</Label><Input data-testid="finance-spese" type="number" step="0.01" value={ff.spese_istruttoria ?? ""} onChange={(e) => setFf({ ...ff, spese_istruttoria: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Tabella finanziaria</Label><Input data-testid="finance-tabella" value={ff.tabella_finanziaria ?? ""} onChange={(e) => setFf({ ...ff, tabella_finanziaria: e.target.value })} placeholder="es. TAN/TAEG" /></div>
            <div className="space-y-1.5"><Label>Assicurazione CPI</Label><Input data-testid="finance-cpi" value={ff.assicurazione_cpi ?? ""} onChange={(e) => setFf({ ...ff, assicurazione_cpi: e.target.value })} placeholder="es. Sì / importo / note" /></div>
            <div className="space-y-1.5"><Label>Assicurazione CVT</Label><Input data-testid="finance-cvt" value={ff.assicurazione_cvt ?? ""} onChange={(e) => setFf({ ...ff, assicurazione_cvt: e.target.value })} placeholder="es. Sì / importo / note" /></div>
            </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinanceOpen(false)}>Annulla</Button>
            <Button data-testid="save-finance-btn" onClick={saveFinance}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle>Modifica Dati Cliente</DialogTitle></DialogHeader>
          {firstAnag && <p className="text-xs text-muted-foreground -mt-2" data-testid="edit-client-anag-note">Le modifiche aggiornano anche l'anagrafica collegata.</p>}
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Tipo soggetto</Label>
              <Select value={cf.soggetto_tipo || "fisica"} onValueChange={(v) => setCf({ ...cf, soggetto_tipo: v })}>
                <SelectTrigger data-testid="client-soggetto-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fisica">Persona Fisica</SelectItem>
                  <SelectItem value="giuridica">Partita IVA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cf.soggetto_tipo === "giuridica" ? (
              <div className="space-y-1.5"><Label>Ragione Sociale</Label><Input data-testid="client-ragione-input" value={cf.ragione_sociale || ""} onChange={(e) => setCf({ ...cf, ragione_sociale: e.target.value })} /></div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="client-nome-input" value={cf.cliente_nome || ""} onChange={(e) => setCf({ ...cf, cliente_nome: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Cognome</Label><Input data-testid="client-cognome-input" value={cf.cliente_cognome || ""} onChange={(e) => setCf({ ...cf, cliente_cognome: e.target.value })} /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="client-email-input" type="email" value={cf.email || ""} onChange={(e) => setCf({ ...cf, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="client-telefono-input" value={cf.telefono || ""} onChange={(e) => setCf({ ...cf, telefono: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Canale</Label><Input data-testid="client-canale-input" value={cf.canale || ""} onChange={(e) => setCf({ ...cf, canale: e.target.value })} placeholder="Es. Passaparola, Web, Filiale…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientOpen(false)}>Annulla</Button>
            <Button data-testid="save-client-btn" onClick={saveClient}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="practice-log-dialog">
          <DialogHeader><DialogTitle>Log Attività Pratica</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {(() => {
              const ev = [];
              (p.client_history || []).forEach((h) => ev.push({ t: h.changed_at, who: h.changed_by, txt: h.changes.map((c) => `${c.field}: ${c.old || "—"} → ${c.new || "—"}`).join("; "), tag: "Modifica" }));
              (p.notes || []).forEach((n) => ev.push({ t: n.created_at, who: n.author_name, txt: `Nota ${n.visibility === "internal" ? "riservata" : "pubblica"}: ${n.text}`, tag: "Nota" }));
              (p.documents || []).forEach((d) => ev.push({ t: d.uploaded_at, who: d.uploaded_by, txt: `Documento: ${d.original_filename}`, tag: "Documento" }));
              (p.provvigioni || []).forEach((x) => ev.push({ t: x.created_at, who: x.created_by, txt: `Provvigione ${x.tipo} ${x.soggetto_nome || ""} (${fmtEuro(x.importo)})`, tag: "Provvigione" }));
              ev.sort((a, b) => new Date(b.t) - new Date(a.t));
              return ev.length ? ev.map((e, i) => (
                <div key={i} className="flex gap-3 border-b border-border/60 pb-2 last:border-0" data-testid={`log-item-${i}`}>
                  <span className="text-[10px] font-semibold rounded-full bg-secondary px-2 py-0.5 h-fit shrink-0">{e.tag}</span>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{e.who} · {fmtDateTime(e.t)}</p>
                    <p className="text-sm break-words">{e.txt}</p>
                  </div>
                </div>
              )) : <p className="text-muted-foreground py-4 text-center">Nessuna attività registrata</p>;
            })()}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!partyUser} onOpenChange={(o) => !o && setPartyUser(null)}>
        <DialogContent data-testid="party-detail-dialog">
          <DialogHeader><DialogTitle>{partyUser?.name}</DialogTitle></DialogHeader>
          {partyUser && (
            <div className="space-y-2 text-sm">
              {[["Ruolo", ROLE_LABELS[partyUser.role] || partyUser.role],
                ["Email", partyUser.email],
                ["Cellulare", partyUser.cellulare],
                ["Telefono fisso", partyUser.telefono_fisso],
                ["Città", partyUser.citta_residenza],
                ["Provincia", partyUser.provincia],
                ["Codice convenzione", partyUser.codice_convenzione],
                ["Utenze finanziarie", partyUser.utenze_finanziarie],
                ["Sede", sedi.find((s) => s.id === partyUser.sede_id)?.nome]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NoteList({ notes, canManage, onDelete, onEdit }) {
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [histId, setHistId] = useState(null);
  if (notes.length === 0) return <p className="text-sm text-muted-foreground py-4 text-center">Nessuna nota</p>;
  const startEdit = (n) => { setEditId(n.id); setDraft(n.text); };
  const saveEdit = async (id) => { if (await onEdit(id, draft)) setEditId(null); };
  return (
    <div className="space-y-3">
      {notes.map((n) => {
        const reserved = n.visibility === "internal";
        const editing = editId === n.id;
        return (
          <div key={n.id} className={`rounded-lg border p-3 ${reserved ? "border-amber-500/60 bg-amber-500/15 border-l-4 border-l-amber-500" : "border-border bg-card"}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-semibold">{n.author_name}</span>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${reserved ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-primary/10 text-primary"}`}>
                  {reserved ? <><Lock className="h-3 w-3" />Riservata</> : <><Globe className="h-3 w-3" />Pubblica</>}
                </span>
                <span className="text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</span>
                {n.edited_at && n.edit_history?.length > 0 && (
                  <button type="button" data-testid={`note-history-btn-${n.id}`} onClick={() => setHistId(histId === n.id ? null : n.id)} className="text-[11px] text-primary hover:underline">· modificata ({n.edit_history.length})</button>
                )}
                {n.edited_at && !(n.edit_history?.length > 0) && <span className="text-[11px] text-muted-foreground">· modificata</span>}
                {canManage && !editing && (
                  <>
                    <button type="button" data-testid={`edit-note-${n.id}`} onClick={() => startEdit(n)} className="text-muted-foreground hover:text-primary transition-colors" title="Modifica nota"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`delete-note-${n.id}`} onClick={() => setConfirmId(n.id)} className="text-muted-foreground hover:text-destructive transition-colors" title="Elimina nota"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
            {editing ? (
              <div className="space-y-2">
                <Textarea data-testid={`edit-note-input-${n.id}`} value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[60px]" />
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" data-testid={`edit-note-cancel-${n.id}`} onClick={() => setEditId(null)}><X className="h-3.5 w-3.5 mr-1" />Annulla</Button>
                  <Button size="sm" data-testid={`edit-note-save-${n.id}`} onClick={() => saveEdit(n.id)}><Check className="h-3.5 w-3.5 mr-1" />Salva</Button>
                </div>
              </div>
            ) : (
              <p className="text-sm whitespace-pre-wrap">{n.text}</p>
            )}
            {histId === n.id && n.edit_history?.length > 0 && (
              <div className="mt-2 border-t border-border pt-2 space-y-1.5" data-testid={`note-history-${n.id}`}>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Cronologia modifiche</p>
                {[...n.edit_history].reverse().map((h, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground">{h.by}</span> · {fmtDateTime(h.at)}
                    <div className="italic">Prima: "{h.old_text || "—"}"</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la nota?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione non può essere annullata. La nota verrà rimossa definitivamente dalla pratica.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-note-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-note-confirm" onClick={() => { onDelete(confirmId); setConfirmId(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
